import {
  CloseCircleOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
  SwapOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Button, InputNumber, Radio, Select, Switch, Tooltip, message } from 'antd'
import type { TFunction } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  composeContinuationSequence,
  createContinuationTask,
  getContinuationSequence,
  getVideoGenConfig,
  videoGenComposedOutputUrl,
  videoGenContinuationFinalFrameUrl,
  videoGenTaskOutputUrl,
  videoGenTaskPosterUrl,
  type ContinuationGenerateBlockReason,
  type ContinuationLastFrameState,
  type ContinuationSequenceContext,
  type PickableVideo,
  type VideoGenConfig,
  type VideoGenTask,
} from '../api'
import { VideoPickerModal } from '../components/VideoPickerModal'
import { TenantVideoPlayerModal } from '../components/TenantVideoPlayerModal'
import { AspectRatioIcon, STATUS_BADGE_CLASS, TaskPosterThumb } from './VideoGenerationPage'
import '../ui/tenant-items.css'
import './video-generation.css'
import './video-continuation.css'

const PLAYER_STALL_MS = 45000
const PLAYER_SLOW_HINT_MS = 12000

/** VISTOERA_VIDEO_CONTINUATION_V1_P0 — "连续生成" page (spec §4-§13).
 *
 * Deliberately reuses `VideoGenerationPage`'s exact aspect-ratio icon,
 * date formatter and status-badge classes (spec §5/§20: "直接复用当前视频
 * 生成页面已有组件、CSS") rather than a parallel copy — this file only
 * adds the pieces genuinely new to continuation (source/final-frame
 * panel, the sequence panel, the video picker).
 *
 * `anchorId` is "the video this page is currently continuing from" —
 * set once on entry (Entry A: `?parent_video_id=` from the video list's
 * 连续生成 action; Entry B: picked via `VideoPickerModal`) and advanced
 * to the freshly created segment after each successful generation (spec
 * §12: the next segment always continues from the *immediate* prior
 * one, not the sequence root) so a tenant can keep adding segments
 * without leaving the page. */
export default function VideoContinuationPage() {
  const { t } = useTranslation(['video', 'actions', 'status', 'common'])
  const { workspaceId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [anchorId, setAnchorId] = useState<string | null>(() => searchParams.get('parent_video_id'))
  const [context, setContext] = useState<ContinuationSequenceContext | null>(null)
  const [contextLoading, setContextLoading] = useState(false)
  const [contextError, setContextError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const [cfg, setCfg] = useState<VideoGenConfig | null>(null)
  const [prompt, setPrompt] = useState('')
  const [duration, setDuration] = useState(10)
  const [enhancePrompt, setEnhancePrompt] = useState(false)
  const [seedMode, setSeedMode] = useState<'random' | 'fixed'>('random')
  const [seed, setSeed] = useState<number | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [composing, setComposing] = useState(false)
  // Spec §12/§21 — the segment *this session* just created from the
  // current anchor, tracked purely client-side (never a backend "active
  // child" concept: this feature intentionally allows multiple sibling
  // branches from the same completed source, so a generic "does the
  // anchor have any in-flight child" cannot drive auto-chaining without
  // also breaking that). Once this specific segment reaches `completed`,
  // the anchor auto-advances onto it — never earlier.
  const [pendingChainSegmentId, setPendingChainSegmentId] = useState<string | null>(null)
  const [playerTaskId, setPlayerTaskId] = useState<string | null>(null)
  const [playerUrl, setPlayerUrl] = useState<string | null>(null)
  const [playerPhase, setPlayerPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [playerSlowHint, setPlayerSlowHint] = useState(false)
  const [playerErrorKind, setPlayerErrorKind] = useState<'timeout' | 'media' | null>(null)
  const [playerReloadNonce, setPlayerReloadNonce] = useState(0)
  const playerSessionRef = useRef(0)
  const playerStallTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const playerSlowTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const playerAutoRetryUsedRef = useRef(false)
  const playerVideoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (!workspaceId) return
    getVideoGenConfig(workspaceId)
      .then((r) => setCfg(r.data))
      .catch(() => undefined)
  }, [workspaceId])

  const loadContext = useCallback(
    async (videoId: string) => {
      setContextLoading(true)
      setContextError(null)
      try {
        const { data } = await getContinuationSequence(workspaceId, videoId)
        setContext(data)
      } catch (e) {
        setContext(null)
        setContextError((e as Error).message || t('video:continuation.toast.loadSourceFailed'))
      } finally {
        setContextLoading(false)
      }
    },
    [workspaceId, t],
  )

  useEffect(() => {
    if (!workspaceId || !anchorId) {
      setContext(null)
      return
    }
    loadContext(anchorId)
  }, [workspaceId, anchorId, loadContext])

  // Poll while (a) the anchor itself hasn't resolved to source_ready yet,
  // or (b) this session is tracking a just-created segment that hasn't
  // reached a terminal state yet — so the sequence panel updates on its
  // own without a manual refresh. Spec §20/§21 — once that specific
  // segment reaches `completed`, auto-chain the anchor onto it (never
  // earlier: this is the exact fix for the bug where the anchor used to
  // jump onto a still-generating segment the instant it was created). A
  // failed/cancelled outcome just stops tracking it — the anchor stays
  // put so the user can retry from the same, still-ready source.
  useEffect(() => {
    if (!anchorId || !context) return
    const anchorNotReady = context.anchor_source_state === 'source_loading'
    if (!anchorNotReady && !pendingChainSegmentId) return
    const pollTimer = window.setInterval(async () => {
      try {
        const { data } = await getContinuationSequence(workspaceId, anchorId)
        setContext(data)
        if (pendingChainSegmentId) {
          const pending = data.segments.find((s) => s.id === pendingChainSegmentId)
          if (pending?.segment_state === 'completed') {
            setPendingChainSegmentId(null)
            setAnchor(pendingChainSegmentId, { keepPrompt: false })
          } else if (pending?.segment_state === 'failed' || pending?.segment_state === 'cancelled') {
            setPendingChainSegmentId(null)
          }
        }
      } catch {
        // transient poll failure — next tick retries; never surface a
        // toast for a background refresh.
      }
    }, 5000)
    return () => window.clearInterval(pollTimer)
  }, [anchorId, context, pendingChainSegmentId, workspaceId])

  const anchorTask = resolveAnchorTask(context, anchorId)

  const setAnchor = (videoId: string | null, opts?: { keepPrompt?: boolean }) => {
    setAnchorId(videoId)
    if (!opts?.keepPrompt) setPrompt('')
    setSeedMode('random')
    setSeed(null)
    setEnhancePrompt(false)
    // A manual anchor change abandons whatever this session was tracking
    // for auto-chain — it no longer applies once the user has navigated
    // elsewhere (re-set fresh by `onSubmit` if they generate again).
    setPendingChainSegmentId(null)
    if (videoId) {
      setSearchParams({ parent_video_id: videoId }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
  }

  const onPickVideo = (video: PickableVideo) => {
    setPickerOpen(false)
    setAnchor(video.id)
  }

  const inheritedAspectRatio = anchorTask?.generation.aspect_ratio
  const inheritedQualityTier = anchorTask?.generation.quality_tier

  const maxDurationForQuality = useMemo(() => {
    if (!cfg || !inheritedQualityTier) return 15
    return cfg.max_duration_seconds[inheritedQualityTier] ?? cfg.max_duration_seconds.default ?? 15
  }, [cfg, inheritedQualityTier])

  useEffect(() => {
    if (duration > maxDurationForQuality) setDuration(maxDurationForQuality)
  }, [maxDurationForQuality, duration])

  const promptMax = cfg?.prompt_max_length ?? 2000

  // Spec §16/§32 — `can_generate_next` is the single canonical decision
  // the backend already computed (source ready + last frame ready + no
  // active child); this only adds the two purely-client-side conditions
  // the backend has no way to know about (prompt text, in-flight submit).
  const canSubmit = !!anchorId && !!context?.can_generate_next && prompt.trim().length > 0 && !submitting

  const clearPlayerTimers = () => {
    if (playerStallTimer.current) {
      clearTimeout(playerStallTimer.current)
      playerStallTimer.current = null
    }
    if (playerSlowTimer.current) {
      clearTimeout(playerSlowTimer.current)
      playerSlowTimer.current = null
    }
  }

  const failPlayer = (session: number, kind: 'timeout' | 'media') => {
    if (playerSessionRef.current !== session) return
    clearPlayerTimers()
    setPlayerPhase('error')
    setPlayerErrorKind(kind)
  }

  const scheduleStallWatchdog = (session: number) => {
    if (playerStallTimer.current) clearTimeout(playerStallTimer.current)
    playerStallTimer.current = setTimeout(() => {
      if (playerSessionRef.current !== session) return
      if (!playerAutoRetryUsedRef.current) {
        playerAutoRetryUsedRef.current = true
        setPlayerReloadNonce((n) => n + 1)
        armPlayerWatchdogs(session)
        return
      }
      failPlayer(session, 'timeout')
    }, PLAYER_STALL_MS)
  }

  const armPlayerWatchdogs = (session: number) => {
    clearPlayerTimers()
    playerSlowTimer.current = setTimeout(() => {
      if (playerSessionRef.current !== session) return
      setPlayerSlowHint(true)
    }, PLAYER_SLOW_HINT_MS)
    scheduleStallWatchdog(session)
  }

  const openPlayer = (taskId: string) => {
    if (!workspaceId) return
    const session = playerSessionRef.current + 1
    playerSessionRef.current = session
    clearPlayerTimers()
    playerAutoRetryUsedRef.current = false
    setPlayerTaskId(taskId)
    setPlayerPhase('loading')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    setPlayerUrl(videoGenTaskOutputUrl(workspaceId, taskId))
    armPlayerWatchdogs(session)
  }

  const retryPlayer = (opts?: { preserveAutoRetryBudget?: boolean }) => {
    if (!playerTaskId || !workspaceId) return
    const session = playerSessionRef.current + 1
    playerSessionRef.current = session
    clearPlayerTimers()
    if (!opts?.preserveAutoRetryBudget) playerAutoRetryUsedRef.current = false
    setPlayerPhase('loading')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    setPlayerUrl(videoGenTaskOutputUrl(workspaceId, playerTaskId))
    setPlayerReloadNonce((n) => n + 1)
    armPlayerWatchdogs(session)
  }

  const closePlayer = () => {
    playerSessionRef.current += 1
    clearPlayerTimers()
    const el = playerVideoRef.current
    if (el) {
      try {
        el.pause()
        el.removeAttribute('src')
        el.load()
      } catch {
        /* ignore cleanup races */
      }
    }
    setPlayerUrl(null)
    setPlayerTaskId(null)
    setPlayerPhase('idle')
    setPlayerSlowHint(false)
    setPlayerErrorKind(null)
    playerAutoRetryUsedRef.current = false
  }

  const handlePlayerReady = () => {
    clearPlayerTimers()
    setPlayerSlowHint(false)
    setPlayerPhase('ready')
  }

  const handlePlayerProgress = () => {
    scheduleStallWatchdog(playerSessionRef.current)
  }

  const handlePlayerError = () => {
    const session = playerSessionRef.current
    if (!playerAutoRetryUsedRef.current) {
      playerAutoRetryUsedRef.current = true
      setTimeout(() => {
        if (playerSessionRef.current !== session) return
        retryPlayer({ preserveAutoRetryBudget: true })
      }, 0)
      return
    }
    failPlayer(session, 'media')
  }

  const onSubmit = async () => {
    if (!anchorId || !canSubmit) return
    setSubmitting(true)
    try {
      const { data: segment } = await createContinuationTask(workspaceId, {
        parent_video_id: anchorId,
        prompt: prompt.trim(),
        duration_seconds: duration,
        seed_mode: seedMode,
        seed: seedMode === 'fixed' ? seed : null,
        enhance_prompt: enhancePrompt,
        continuation_mode: 'last_frame_i2v',
      })
      message.success(t('video:continuation.toast.queued'))
      // Spec §6/§12/§20 — deliberately do NOT advance the anchor onto the
      // segment just created: it is still queued, not `source_ready`, and
      // cannot become the *next* source until it actually completes. Just
      // remember its id so the polling effect above can auto-chain onto
      // it once (and only once) it does. Reloading the *current* anchor's
      // context is what makes the new segment appear in the sequence
      // panel right away.
      setPendingChainSegmentId(segment.id)
      await loadContext(anchorId)
    } catch (e) {
      message.error((e as Error).message || t('video:continuation.toast.createFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const onCompose = async () => {
    if (!anchorId) return
    setComposing(true)
    try {
      await composeContinuationSequence(workspaceId, anchorId)
      message.success(t('video:continuation.toast.composeSuccess'))
      await loadContext(anchorId)
    } catch (e) {
      message.error((e as Error).message || t('video:continuation.toast.composeFailed'))
    } finally {
      setComposing(false)
    }
  }

  const chain: VideoGenTask[] = context ? [context.root, ...context.segments] : []

  return (
    <div className="tc-items-page tc-video-gen-page tc-video-continuation-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('video:continuation.title')}</h2>
          <p className="tc-page-desc">{t('video:continuation.subtitle')}</p>
        </div>
      </div>

      <div className="tc-video-continuation-layout">
        <div className="tc-video-continuation-main">
          <div className="tc-video-continuation-top-row">
            <div className="tc-data-card tc-video-continuation-panel">
              <div className="tc-video-gen-card-title">{t('video:continuation.sourceVideo')}</div>
              {!anchorId ? (
                <EmptySourceState onPick={() => setPickerOpen(true)} />
              ) : contextLoading && !context ? (
                <div className="tc-video-continuation-loading">{t('common:loading')}</div>
              ) : contextError ? (
                <div className="tc-video-gen-error">{contextError}</div>
              ) : context?.anchor_source_state === 'source_ready' && anchorTask ? (
                <SourceVideoCard workspaceId={workspaceId} task={anchorTask} onChangeSource={() => setPickerOpen(true)} />
              ) : context?.anchor_source_state === 'source_loading' ? (
                <div className="tc-video-continuation-loading">{t('video:continuation.sourceLoading')}</div>
              ) : context ? (
                // Spec §3/§34/§35 — a genuinely unusable source (invalid/
                // failed), reported as its own real state, never re-worded
                // to look like a last-frame or segment failure.
                <div className="tc-video-gen-error">
                  {context.anchor_source_state === 'source_failed'
                    ? t('video:continuation.sourceError.failed')
                    : t('video:continuation.sourceError.unavailable')}
                </div>
              ) : null}
            </div>

            <div className="tc-data-card tc-video-continuation-panel">
              <div className="tc-video-gen-card-title">{t('video:continuation.finalFrame')}</div>
              {!anchorId || !context ? (
                <div className="tc-video-continuation-empty-frame">{t('video:continuation.selectSourceForFrame')}</div>
              ) : context.anchor_source_state !== 'source_ready' ? (
                <div className="tc-video-continuation-empty-frame">{t('video:continuation.waitSourceForFrame')}</div>
              ) : (
                <FinalFramePreview
                  workspaceId={workspaceId}
                  videoId={anchorId}
                  canonicalState={context.anchor_last_frame.state}
                  onSettled={() => loadContext(anchorId)}
                />
              )}
              <div className="tc-video-continuation-hint">{t('video:continuation.inheritHint')}</div>
            </div>
          </div>

          <div className="tc-data-card tc-video-continuation-form-card">
            <div className="tc-video-gen-card-title">{t('video:continuation.generationSettings')}</div>

            <label className="tc-video-gen-label">{t('video:generation.promptLabel')}</label>
            <textarea
              className="tc-video-continuation-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, promptMax))}
              placeholder={t('video:continuation.promptPlaceholder')}
              rows={4}
              disabled={!anchorId}
            />
            <div className="tc-video-gen-char-count">
              {prompt.length}/{promptMax}
            </div>

            <label className="tc-video-gen-label">{t('video:generation.referenceMaterialsLabel')}</label>
            <div className="tc-video-gen-refs-row">
              <DisabledRefCard kind="image" />
              <DisabledRefCard kind="video" />
            </div>

            <div className="tc-video-gen-params-row">
              <div className="tc-video-gen-param">
                <label>{t('video:generation.durationLabel')}</label>
                {/* Spec §12/§13/§14 — free numeric input, not a fixed
                    3/5/10 preset dropdown; same canonical `duration_seconds`
                    field + `max_duration_for_quality` cap the normal video
                    generation page already enforces server-side (spec §13:
                    "不要给连续生成重新发明一套 duration contract"), just a
                    different widget for this page. */}
                <InputNumber
                  value={duration}
                  onChange={(v) => setDuration(typeof v === 'number' ? v : 1)}
                  min={1}
                  max={maxDurationForQuality}
                  step={1}
                  precision={1}
                  disabled={!anchorId}
                  addonAfter={t('video:continuation.secondsSuffix')}
                  style={{ width: 100 }}
                />
              </div>
              <div className="tc-video-gen-param">
                <label>{t('video:continuation.inheritedParams')}</label>
                <div className="tc-video-continuation-inherited">
                  {inheritedAspectRatio && <AspectRatioIcon ratio={inheritedAspectRatio} size={16} />}
                  <span>
                    {t('video:continuation.inheritedFromSource', {
                      quality: inheritedQualityTier ?? '—',
                      ratio: inheritedAspectRatio ?? '—',
                    })}
                  </span>
                </div>
              </div>
              <div className="tc-video-gen-param">
                <label>{t('video:generation.generationCountLabel')}</label>
                <Select value={1} disabled style={{ width: 70 }} options={[{ value: 1, label: '1' }]} />
              </div>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={submitting}
                disabled={!canSubmit}
                onClick={onSubmit}
                className="tc-video-gen-submit"
              >
                {t('video:continuation.startNextSegment')}
              </Button>
            </div>

            {anchorId && context && !context.can_generate_next && (
              <div className="tc-video-continuation-blocked-note">{generateBlockReasonMessage(context.generate_block_reason, t)}</div>
            )}

            <div className="tc-video-gen-advanced-toggle" onClick={() => setAdvancedOpen((v) => !v)}>
              {t('video:generation.advancedToggle')} {advancedOpen ? '▲' : '▼'}
            </div>
            {advancedOpen && (
              <div className="tc-video-gen-advanced">
                <div className="tc-video-gen-param">
                  <label>{t('video:generation.enhancePromptLabel')}</label>
                  <Switch checked={enhancePrompt} onChange={setEnhancePrompt} size="small" />
                </div>
                <div className="tc-video-gen-param">
                  <label>Seed</label>
                  <Radio.Group value={seedMode} onChange={(e) => setSeedMode(e.target.value)} size="small">
                    <Radio.Button value="random">{t('video:generation.seedRandom')}</Radio.Button>
                    <Radio.Button value="fixed">{t('video:generation.seedFixed')}</Radio.Button>
                  </Radio.Group>
                </div>
                {seedMode === 'fixed' && (
                  <InputNumber
                    value={seed ?? undefined}
                    onChange={(v) => setSeed(v ?? null)}
                    min={cfg?.seed_range.min ?? 0}
                    max={cfg?.seed_range.max ?? 2147483647}
                    placeholder={t('video:generation.seedPlaceholder')}
                    style={{ width: 160 }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="tc-data-card tc-video-continuation-sequence-panel">
          <div className="tc-video-gen-card-title">
            {t('video:continuation.sequenceTitle', { stage: sequenceStageLabel(context, t) })}
          </div>
          {!anchorId ? (
            <div className="tc-video-continuation-empty-frame">{t('video:continuation.selectSourceForSequence')}</div>
          ) : (
            <>
              <div className="tc-video-continuation-chain">
                {chain.map((item, idx) => (
                  <div key={item.id} className="tc-video-continuation-chain-item">
                    {idx > 0 && <div className="tc-video-continuation-chain-connector" aria-hidden="true" />}
                    <button
                      type="button"
                      className={`tc-video-continuation-chain-card${item.id === anchorId ? ' is-active' : ''}`}
                      onClick={() => setAnchor(item.id)}
                    >
                      <TaskPosterThumb
                        src={segmentPosterUrl(workspaceId, item)}
                        onClick={() => {
                          if (item.result_asset_ready) openPlayer(item.id)
                        }}
                      />
                      <div className="tc-video-continuation-chain-meta">
                        <div className="tc-video-continuation-chain-title">
                          {idx === 0
                            ? t('video:continuation.sourceVideoLabel')
                            : t('video:continuation.segmentLabel', { index: item.segment_index })}
                        </div>
                        <div className="tc-video-continuation-chain-sub">
                          {t('video:generation.table.parametersValue', {
                            seconds: item.generation.duration_seconds,
                            quality: item.generation.quality_tier,
                            ratio: item.generation.aspect_ratio,
                          })}
                        </div>
                        <span className={`tc-badge ${STATUS_BADGE_CLASS[item.status] || 'tc-badge-neutral'}`}>
                          {segmentStatusLabel(item, t)}
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
                <div className="tc-video-continuation-chain-item">
                  <div className="tc-video-continuation-chain-connector" aria-hidden="true" />
                  <div className="tc-video-continuation-chain-next">
                    <PlusOutlined />
                    <div>
                      <div className="tc-video-continuation-chain-title">
                        {context?.can_generate_next
                          ? t('video:continuation.nextPending')
                          : t('video:continuation.nextBlocked')}
                      </div>
                      <div className="tc-video-continuation-chain-sub">{t('video:continuation.nextHint')}</div>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                block
                icon={<SwapOutlined />}
                className="tc-video-continuation-change-source"
                onClick={() => setPickerOpen(true)}
              >
                {t('video:continuation.changeSource')}
              </Button>

              <div className="tc-video-continuation-compose-section">
                <div className="tc-video-gen-card-title" style={{ marginBottom: 6 }}>
                  {t('video:continuation.composeOutput')}
                  {context?.is_composed
                    ? t('video:continuation.composeDone')
                    : context?.can_compose
                      ? t('video:continuation.composeReady')
                      : t('video:continuation.composeNotReady')}
                </div>
                <div className="tc-video-continuation-hint">{t('video:continuation.composeHint')}</div>
                {(() => {
                  const hints = composeHintLines(context, t)
                  return (
                    <>
                      {hints.primary ? <div className="tc-video-continuation-hint">{hints.primary}</div> : null}
                      <div className="tc-video-continuation-hint">{hints.secondary}</div>
                    </>
                  )
                })()}
                <Button
                  type="primary"
                  block
                  loading={composing}
                  disabled={!context?.can_compose}
                  onClick={onCompose}
                  className="tc-video-continuation-compose-btn"
                >
                  {t('video:continuation.composeButton')}
                </Button>
                {context?.sequence_id && (
                  <a
                    className="tc-video-continuation-composed-link"
                    href={videoGenComposedOutputUrl(workspaceId, anchorId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('video:continuation.viewComposed')}
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <TenantVideoPlayerModal
        open={!!playerTaskId}
        onClose={closePlayer}
        title={t('video:continuation.playerTitle')}
        videoUrl={playerUrl}
        posterUrl={workspaceId && playerTaskId ? videoGenTaskPosterUrl(workspaceId, playerTaskId) : null}
        phase={playerPhase}
        slowHint={playerSlowHint}
        errorKind={playerErrorKind}
        reloadNonce={playerReloadNonce}
        videoRef={playerVideoRef}
        onReady={handlePlayerReady}
        onProgress={handlePlayerProgress}
        onError={handlePlayerError}
        onRetry={() => retryPlayer()}
      />

      <VideoPickerModal
        open={pickerOpen}
        workspaceId={workspaceId}
        excludeVideoId={anchorId}
        onCancel={() => setPickerOpen(false)}
        onConfirm={onPickVideo}
      />
    </div>
  )
}

/** Spec §16/§32 — one canonical mapping from the backend's
 * `generate_block_reason` to display text, replacing the single
 * hardcoded "源视频尚未完成" string that used to fire regardless of the
 * *actual* reason (the root cause of the reported contradiction: a
 * last-frame extraction failure was worded as if the source itself were
 * broken, when the two are decoupled domains — spec §34). */
function segmentPosterUrl(workspaceId: string, task: VideoGenTask) {
  const stamp = task.result_asset_ready ? 'ready' : task.segment_state || task.status
  return `${videoGenTaskPosterUrl(workspaceId, task.id)}?v=${encodeURIComponent(stamp)}`
}

function segmentStatusLabel(task: VideoGenTask, t: TFunction): string {
  if (task.status === 'succeeded' && !task.result_asset_ready) return t('video:continuation.segmentStatus.processing')
  return t(`status:${task.status}`, { defaultValue: task.status_label })
}

function generateBlockReasonMessage(reason: ContinuationGenerateBlockReason, t: TFunction): string {
  switch (reason) {
    case 'LAST_FRAME_NOT_READY':
      return t('video:continuation.blockReason.lastFrameNotReady')
    case 'SOURCE_INVALID':
      return t('video:continuation.blockReason.sourceInvalid')
    case 'SOURCE_NOT_READY':
    default:
      return t('video:continuation.blockReason.sourceNotReady')
  }
}

function composeHintLines(
  context: ContinuationSequenceContext | null,
  t: TFunction,
): { primary: string; secondary: string } {
  const genReady =
    context?.compose_ready_generated_segment_count ?? context?.compose_ready_segment_count ?? 0
  const videoCount =
    context?.compose_ready_video_count ?? genReady + (context?.root?.result_asset_ready ? 1 : 0)
  if (context?.can_compose) {
    return {
      primary: t('video:continuation.composeHints.ready', { count: videoCount }),
      secondary: t('video:continuation.composeHints.readyDetail', { count: genReady }),
    }
  }
  if (genReady === 0) {
    return {
      primary: '',
      secondary: t('video:continuation.composeHints.needOneSegment'),
    }
  }
  return {
    primary: t('video:continuation.composeHints.currentReady', { count: genReady }),
    secondary: t('video:continuation.composeHints.waitAllReady'),
  }
}

/** Spec §19 — "draft" only really means "nothing has ever been generated
 * yet"; the moment any segment exists this is no longer a draft, whether
 * it is still generating or already composed. */
function sequenceStageLabel(context: ContinuationSequenceContext | null, t: TFunction): string {
  if (!context || context.segments.length === 0) return t('video:continuation.stages.draft')
  if (context.is_composed) return t('video:continuation.stages.done')
  if (context.can_compose) return t('video:continuation.stages.composable')
  return t('video:continuation.stages.inProgress')
}

function resolveAnchorTask(context: ContinuationSequenceContext | null, anchorId: string | null): VideoGenTask | null {
  if (!context || !anchorId) return null
  if (context.root.id === anchorId) return context.root
  // Root-cause fix (VISTOERA_CONTINUOUS_VIDEO_STATE_MACHINE_AND_LAST_FRAME_P0)
  // — this used to fall back to `context.root` when `anchorId` wasn't
  // found yet (e.g. `context` is still the *previous* anchor's stale
  // response while a new one is loading). That silently displayed the
  // wrong video as "①源视频" while `anchor_source_state`/`can_generate_next`
  // were already being computed for the *new*, different anchor — the
  // exact split-brain contradiction reported. Returning `null` here lets
  // the caller render a real loading state instead of a stale substitute.
  return context.segments.find((s) => s.id === anchorId) ?? null
}

function EmptySourceState({ onPick }: { onPick: () => void }) {
  const { t } = useTranslation(['video', 'actions', 'status', 'common'])
  return (
    <div className="tc-video-continuation-empty-source" onClick={onPick} role="button" tabIndex={0}>
      <PlusOutlined />
      <div>{t('video:continuation.emptySource.pickVideo')}</div>
      <div className="tc-video-continuation-hint">{t('video:continuation.emptySource.hint')}</div>
    </div>
  )
}

function SourceVideoCard({
  workspaceId,
  task,
  onChangeSource,
}: {
  workspaceId: string
  task: VideoGenTask
  onChangeSource: () => void
}) {
  const { t } = useTranslation(['video', 'actions', 'status', 'common'])
  return (
    <div className="tc-video-continuation-source-card">
      <video
        controls
        poster={videoGenTaskPosterUrl(workspaceId, task.id)}
        src={videoGenTaskOutputUrl(workspaceId, task.id)}
        className="tc-video-continuation-source-video"
      />
      <div className="tc-video-continuation-source-meta">
        <div className="tc-video-continuation-source-prompt" title={task.prompt}>
          {task.prompt || t('video:continuation.noPrompt')}
        </div>
        <div className="tc-video-continuation-source-params">
          {t('video:generation.table.parametersValue', {
            seconds: task.generation.duration_seconds,
            quality: task.generation.quality_tier,
            ratio: task.generation.aspect_ratio,
          })}
        </div>
        <Button size="small" icon={<SwapOutlined />} onClick={onChangeSource}>
          {t('video:continuation.changeSource')}
        </Button>
      </div>
    </div>
  )
}

/** VISTOERA_CONTINUOUS_VIDEO_STATE_MACHINE_AND_LAST_FRAME_P0 (spec §14/
 * §16/§34/§36/§37) — `canonicalState` (the backend's own persisted
 * `final_frame_storage_key`/`final_frame_failed_at`-derived truth, from
 * `context.anchor_last_frame.state`) decides the *displayed* state up
 * front, instead of the old design where an `<img onError>` was the only
 * signal — that meant a failure caused by the video simply not being
 * finished yet (a 404 for an unrelated reason) rendered the exact same
 * "最后一帧提取失败" text as a genuine extraction failure. The `<img>` is
 * still the actual mechanism that triggers extraction + displays bytes,
 * but only mounted when there is a real reason to attempt/re-attempt one
 * (`not_requested` or an explicit "重试" click) — a canonically `failed`
 * state renders the failure immediately without a doomed extra request,
 * and per spec §36 never auto-retries on its own. */
function FinalFramePreview({
  workspaceId,
  videoId,
  canonicalState,
  onSettled,
}: {
  workspaceId: string
  videoId: string
  canonicalState: ContinuationLastFrameState
  onSettled: () => void
}) {
  const { t } = useTranslation(['video', 'actions', 'status', 'common'])
  const [attempting, setAttempting] = useState(canonicalState !== 'failed')
  const [displayReady, setDisplayReady] = useState(canonicalState === 'ready')
  // Spec §20 — a real, retriable failure state, not a permanent
  // placeholder: bumping `attempt` changes the `<img>` `src` (cache-busted
  // via query param) so the browser re-issues the request instead of
  // replaying a cached 404.
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    setAttempting(canonicalState !== 'failed')
    setDisplayReady(canonicalState === 'ready')
    setAttempt(0)
  }, [videoId, canonicalState])

  const retry = () => {
    setAttempting(true)
    setDisplayReady(false)
    setAttempt((a) => a + 1)
  }
  const src = videoGenContinuationFinalFrameUrl(workspaceId, videoId) + (attempt > 0 ? `?retry=${attempt}` : '')
  return (
    <div className="tc-video-continuation-frame-preview">
      {attempting ? (
        <img
          key={`${videoId}-${attempt}`}
          src={src}
          alt={t('video:continuation.finalFrameAlt')}
          onLoad={() => {
            setDisplayReady(true)
            onSettled()
          }}
          onError={() => {
            setAttempting(false)
            setDisplayReady(false)
            onSettled()
          }}
          style={{ opacity: displayReady ? 1 : 0.4 }}
        />
      ) : (
        <div className="tc-video-continuation-empty-frame tc-video-continuation-frame-failed">
          <CloseCircleOutlined /> {t('video:continuation.extractFailed')}
          <Button size="small" onClick={retry}>
            {t('actions:retry')}
          </Button>
        </div>
      )}
      {attempting && !displayReady && (
        <div className="tc-video-continuation-frame-loading">
          <LoadingOutlined /> {t('video:continuation.extracting')}
        </div>
      )}
    </div>
  )
}

function DisabledRefCard({ kind }: { kind: 'image' | 'video' }) {
  const { t } = useTranslation(['video', 'actions', 'status', 'common'])
  const label =
    kind === 'image' ? t('video:generation.referenceImage') : t('video:generation.referenceVideo')
  return (
    <Tooltip title={t('video:continuation.refDisabledTooltip')} overlayClassName="tc-video-gen-tooltip">
      <div className="tc-video-gen-ref-card tc-video-continuation-ref-disabled">
        <LockOutlined className="tc-video-gen-ref-icon" />
        <div>{label}</div>
        <div className="tc-video-gen-ref-hint">{t('video:continuation.refDisabledHint')}</div>
      </div>
    </Tooltip>
  )
}